'use client'

import { ReactNode, CSSProperties, useEffect, useState } from 'react'

export interface MotionFadeInProps {
  children: ReactNode
  className?: string
  style?: CSSProperties
  initialY?: number
  duration?: number
  delay?: number
  once?: boolean
  viewportMargin?: string
}

export default function MotionFadeIn({
  children,
  className = '',
  style,
  initialY = 30,
  duration = 0.8,
  delay = 0,
  once = true,
  viewportMargin = '-100px',
}: MotionFadeInProps) {
  const [motion, setMotion] = useState<any>(null)
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    // Dynamically import framer-motion only when component mounts
    import('framer-motion')
      .then((mod) => {
        // Ensure motion and motion.div exist before setting
        if (mod?.motion && typeof mod.motion === 'object' && 'div' in mod.motion) {
          setMotion(mod.motion)
          setIsLoaded(true)
        } else {
          setIsLoaded(true) // Still render children even if motion fails
        }
      })
      .catch((error) => {
        console.warn('Failed to load framer-motion:', error)
        setIsLoaded(true) // Still render children even if motion fails
      })
  }, [])

  // If motion is not loaded yet, render children without animation
  if (!isLoaded || !motion || !motion.div) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    )
  }

  const MotionDiv = motion.div

  return (
    <MotionDiv
      className={className}
      style={style}
      initial={{ opacity: 0, y: initialY }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: viewportMargin }}
      transition={{ duration, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      {children}
    </MotionDiv>
  )
}
